import { Skeleton } from 'antd';
import type { WorkingReportData } from '../../../../../../app/features/employee/emp-attendance/emp-attendance.type';
import useCarousel from '../../../../../../hooks/useCarousel';
import CalenderIcon from '../../_icons/CalenderIcon';
import LeftArrow from '../../_icons/LeftArrow';
import RightArrow from '../../_icons/RightArrow';
import UsersIcon from '../../_icons/UsersIcon';

type Props = {
  data?: WorkingReportData['dashboard'];
  isLoading?: boolean;
};

// Helper function to format minutes
const formatMinutes = (minutes: number) => {
  if (minutes === 0) return '0H 0M';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}H ${mins}M`;
};

export default function TopBar({ data, isLoading }: Props) {
  const { scrollRef, scrollRight, scrollLeft } = useCarousel();

  const list = [
    {
      count: data?.totalOnTimeUsers || 0,
      title: 'On time check in',
      icon: <UsersIcon />,
    },
    {
      count: data?.totalCheckedOutUsers || 0,
      title: 'Check Out',
      icon: <CalenderIcon />,
    },
    {
      count: formatMinutes(data?.totalWorkingHoursInMinutes || 0),
      title: 'Work Hours',
      icon: <CalenderIcon />,
    },
    {
      count: formatMinutes(data?.totalBreakTimeInMinutes || 0),
      title: 'Break',
      icon: <CalenderIcon />,
    },
    {
      count: formatMinutes(data?.totalIdleTimeInMinutes || 0),
      title: 'Idle Time',
      icon: <CalenderIcon />,
    },
    {
      count: formatMinutes(data?.totalOvertimeInMinutes || 0),
      title: 'Over Time',
      icon: <CalenderIcon />,
    },
  ];

  if (isLoading) {
    return (
      <div className="flex gap-4 mb-6 overflow-hidden">
        {[...Array(6)].map((_, idx) => (
          <div
            key={idx}
            className="flex-shrink-0 w-52 h-52 p-5 bg-theme-slate-800 rounded-xl border border-theme-gray-700"
          >
            <Skeleton.Avatar active size={40} shape="square" />
            <br />
            <Skeleton.Input
              active
              size="small"
              className="mt-3 mb-2"
              style={{ width: 80 }}
            />
            <Skeleton.Input active size="small" style={{ width: 120 }} />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="relative group attendance-top-bar">
      {/* Left Arrow */}
      <button
        onClick={scrollLeft}
        className="absolute -left-5 top-1/2 -translate-y-1/2 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 arrow"
      >
        <LeftArrow />
      </button>

      {/* Scrollable container */}
      <div
        ref={scrollRef}
        className="flex gap-4 mb-6 overflow-x-auto scroll-smooth hide-scrollbar"
        style={{ scrollSnapType: 'x mandatory' }}
      >
        {list.map((el, idx) => (
          <div
            key={idx}
            className="flex-shrink-0 w-52 h-52 p-5 bg-theme-slate-800 rounded-xl border border-theme-gray-700 dark:text-white flex flex-col justify-center scroll-snap-align-start"
          >
            <div>
              <div>{el.icon}</div>
              <h6 className="text-xl font-bold mt-3 mb-1">{el.count}</h6>
              <p className="text-base capitalize">{el.title}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Right Arrow */}
      <button
        onClick={scrollRight}
        className="absolute -right-5 top-1/2 -translate-y-1/2 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 arrow"
      >
        <RightArrow />
      </button>
    </div>
  );
}
